import { getChunks } from 'common';
import { CellMeasurerCache, List } from 'react-virtualized';
import { debounce } from 'throttle-debounce';
import { ScreenOrientationType } from '../../../../../hooks/useScreenOrientation';
import {
  CfDataGridCardViewDefinition,
  CfDataGridForStoreDataProps,
  CfDataGridProps,
  CfRowData,
  NavigableElement,
} from '../../../CfDataGridProps';
import { getColumnCount, getHasItems, getItemIds, getItems } from '../../../utility/data-grid-props';
import { CfDataGridNoResultsFound } from '../../DataGridNoResultsFound/CfDataGridNoResultsFound';
import { CfDataGridCardRow } from '../card-row/CfDataGridCardRow';
import { CfDataGridCardRowByItemId } from '../card-row/CfDataGridCardRowByItemId';
import { CfDataGridCardRowBaseProps } from '../card-row/common';

// Helper
export function getCardRowDataById<TItem, TAppState>(
  baseProps: CfDataGridCardRowBaseProps<TItem>,
  itemIds: { itemId: string | undefined; isLoading: boolean; after?: boolean }[],
  dataGridProps: CfDataGridForStoreDataProps<TItem, TAppState>
): CfRowData | undefined {
  if (itemIds.length <= 0) return;
  return {
    type: 'card-row',
    content: <CfDataGridCardRowByItemId baseProps={baseProps} itemIds={itemIds} dataGridProps={dataGridProps} />,
  } as CfRowData;
}

// Helper
export function getCardRowData<TItem>(
  baseProps: CfDataGridCardRowBaseProps<TItem>,
  items: (TItem | undefined)[],
  dataGridProps: CfDataGridProps<TItem>
): CfRowData | undefined {
  if (items.length <= 0) return;
  return {
    type: 'card-row',
    content: <CfDataGridCardRow baseProps={baseProps} items={items} dataGridProps={dataGridProps} />,
  } as CfRowData;
}

export const getVirtualizedCardRows = <TItem, TAppState>(
  dataGridProps: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>,
  cardViewDefinition: CfDataGridCardViewDefinition<TItem>,
  setNavigableElement: (navigableElement: NavigableElement, rowIndex: number, columnIndex: number) => void,
  orientation: ScreenOrientationType,
  recomputeRowHeightsByRowIndex: (idx: number) => void,
  debouncedRecomputeRowHeights: debounce<() => void>
): { itemIds: string[]; rows: CfRowData[] } => {
  const itemIds: string[] = [];
  const rows: CfRowData[] = [];
  //console.log('get card rows');

  const columnCount = getColumnCount(cardViewDefinition, orientation);

  if (!getHasItems(dataGridProps)) {
    rows.push({
      type: 'row',
      content: (
        <CfDataGridNoResultsFound
          message={dataGridProps.noItemsMessage}
          sx={dataGridProps.noItemsMessageSx}
          displayMode='card'
        />
      ),
    });
  } else {
    if ('data' in dataGridProps) {
      const items = getItems(dataGridProps.data);
      const rowItems = getChunks(items, columnCount);
      rowItems.forEach((items: { item: TItem | undefined; isLoading: boolean }[], rowIndex: number) => {
        const ids: string[] = items.map((i) => (i.item ? dataGridProps.selectId(i.item) ?? '' : ''));
        itemIds.push(...ids);
        const newRow = getCardRowData(
          {
            rowIndex,
            cardViewDefinition: cardViewDefinition,
            setNavigableElement: setNavigableElement,
            recomputeRowHeights: debouncedRecomputeRowHeights,
            recomputeRowHeight: () => recomputeRowHeightsByRowIndex(rowIndex),
            orientation: orientation,
          },
          items.map((i) => i.item),
          dataGridProps
        );
        if (newRow) rows.push(newRow);
      });
    } else {
      const itemIdsWithLoading = getItemIds(dataGridProps.storeData, cardViewDefinition.afterItemIds);
      const rowItemIds = getChunks(itemIdsWithLoading, columnCount);
      rowItemIds.forEach(
        (items: { itemId: string | undefined; isLoading: boolean; after?: boolean }[], rowIndex: number) => {
          const newRow = getCardRowDataById(
            {
              rowIndex,
              cardViewDefinition: cardViewDefinition,
              setNavigableElement: setNavigableElement,
              recomputeRowHeights: debouncedRecomputeRowHeights,
              recomputeRowHeight: () => recomputeRowHeightsByRowIndex(rowIndex),
              orientation: orientation,
            },
            items,
            dataGridProps
          );
          if (newRow) rows.push(newRow);
        }
      );
    }
  }
  return { itemIds, rows };
};

export const detectCardRowsChanged = (
  itemIds: string[],
  prevItemIds: string[] | undefined
): { index: number; differenceFound: boolean } => {
  const index = 0;
  let differenceFound = false;

  for (let index = 0; index < itemIds.length; index++) {
    if (prevItemIds === undefined) break;
    if (prevItemIds[index] !== itemIds[index]) {
      index = index;
      differenceFound = true;
      break;
    }
  }

  return { index, differenceFound };
};

export const recomputeCardRowHeights = (
  listRef: React.MutableRefObject<List | null>,
  cache: CellMeasurerCache
): void => {
  if (listRef.current) {
    cache.clearAll();
    listRef.current?.forceUpdateGrid();

    //console.log('recompute row heights');
  }
};

export const recomputeCardRowHeightsFromIndex = (
  listRef: React.MutableRefObject<List | null>,
  cache: CellMeasurerCache,
  columnCount: number,
  length: number,
  i: number
): void => {
  if (listRef.current) {
    const start = Math.floor(i / columnCount);
    const end = Math.floor(length / columnCount);

    for (let i = start; i <= end; i++) cache.clear(i, 0);
    listRef.current.forceUpdateGrid();

    //console.log('recompute row heights from', i);
  }
};

export const recomputeCardRowHeightsByIndex = (
  listRef: React.MutableRefObject<List | null>,
  cache: CellMeasurerCache,
  i: number
): void => {
  if (listRef.current) {
    if (i > 0) cache.clear(i - 1, 0);
    cache.clear(i, 0);
    listRef.current.forceUpdateGrid();

    //console.log('recompute row heights by', i);
  }
};
